import { useApp } from "../store.jsx";
import { taskEconomics, fmtSek, fmtMonth, fmtMonths } from "../calc.js";

export default function FinancialImpact({ task }) {
  const { settings } = useApp();
  if (!task || !settings) return null;
  const e = taskEconomics(task, settings);

  const rows = [
    ["Hours saved",      fmtSek(e.valueBreakdown.hoursSavedSek), `${Number(task.monthlyHoursSaved).toFixed(0)} h × ${Number(settings.hourlyRateSek).toFixed(0)} SEK/h`],
    ["Net new revenue",  fmtSek(e.valueBreakdown.newRevenueSek), ""],
  ];

  const endValueClass = e.horizonEndValueSek >= 0 ? "positive" : "negative";

  const fQ = Number(settings.factorQuality)   || 0;
  const fS = Number(settings.factorStrategic) || 0;
  const fC = Number(settings.factorCustomer)  || 0;
  const factorSum = fQ + fS + fC;
  const scoreSubtitle = factorSum > 0
    ? `weighted: Q×${fQ.toFixed(2)} · S×${fS.toFixed(2)} · C×${fC.toFixed(2)}`
    : "simple average (all factors zero)";

  return (
    <section className="brief">
      <h2>Financial impact</h2>
      <div className="kpi-row">
        <div className="kpi">
          <div className="l">One-time cost</div>
          <div className="v">{fmtSek(e.costSek)}</div>
          <div className="s">{Number(task.effortPw).toFixed(1)} pw × 40 h × {Number(settings.hourlyRateSek).toFixed(0)} SEK/h</div>
        </div>
        <div className="kpi">
          <div className="l">Monthly value</div>
          <div className="v">{fmtSek(e.monthlyValueSek)}</div>
          <div className="s">recurring, once running</div>
        </div>
        <div className="kpi">
          <div className="l">Saved-hours value /mo</div>
          <div className="v positive">{fmtSek(e.valueBreakdown.hoursSavedSek)}</div>
          <div className="s">hours saved × hourly rate</div>
        </div>
        <div className="kpi">
          <div className="l">New revenue /mo</div>
          <div className="v positive">{fmtSek(e.valueBreakdown.newRevenueSek)}</div>
          <div className="s">recurring once live</div>
        </div>
        <div className="kpi">
          <div className="l">New revenue to Dec 2027</div>
          <div className="v positive">{fmtSek(e.newRevenueHorizonSek)}</div>
          <div className="s">cumulative while live</div>
        </div>
        <div className="kpi">
          <div className="l">Weighted avg impact score</div>
          <div className="v">{e.weightedAvgScore.toFixed(2)}</div>
          <div className="s">{scoreSubtitle}</div>
        </div>
        <div className="kpi">
          <div className="l">Break-even after</div>
          <div className="v">{fmtMonths(e.monthsToBreakeven)}</div>
          <div className="s">absolute: {fmtMonth(e.breakevenMonth)}</div>
        </div>
        <div className="kpi">
          <div className="l">Dec 2027 net value</div>
          <div className={"v " + endValueClass}>{fmtSek(e.horizonEndValueSek)}</div>
          <div className="s">vs. doing nothing</div>
        </div>
      </div>

      <div style={{marginTop:18}}>
        <h3 style={{fontSize:13, color:"var(--muted)", textTransform:"uppercase", letterSpacing:".05em"}}>
          Monthly value breakdown
        </h3>
        <div className="value-breakdown" style={{marginTop:8}}>
          {rows.map(([k, v, s], i) => (
            <span key={i} style={{display:"contents"}}>
              <span className="lab">{k}{s ? <span style={{color:"var(--muted)",fontSize:11}}> · {s}</span> : null}</span>
              <span className="v">{v}</span>
            </span>
          ))}
          <span className="lab" style={{fontWeight:600, color:"#fff", marginTop:6, borderTop:"1px solid var(--grid)", paddingTop:6}}>Total</span>
          <span className="v" style={{fontWeight:600, marginTop:6, borderTop:"1px solid var(--grid)", paddingTop:6}}>
            {fmtSek(e.monthlyValueSek)}
          </span>
        </div>
        <p style={{color:"var(--muted)",fontSize:12,marginTop:10}}>
          Impact scores (quality / importance / customer) feed the <b>weighted average</b> above
          but no longer contribute monetary value. Adjust their weights in Settings.
        </p>
      </div>

      <div className="divider"/>
      <p style={{color:"var(--muted)",fontSize:12,margin:0}}>
        Calendar duration: <b style={{color:"#fff"}}>{fmtMonths(e.durationMonths)}</b> &nbsp;·&nbsp;
        Completion: <b style={{color:"#fff"}}>{fmtMonth(e.completionMonth)}</b> &nbsp;·&nbsp;
        Savings only accrue after completion.
      </p>
    </section>
  );
}
